import { LockOpenOutlined, LockOutlined } from '@mui/icons-material'
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { ConfirmDialog } from '@omnea-app/frontend/components/layout/dialog/ConfirmDialog'
import { FeatureFlagsEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { useRequestRecordPermissions } from '../../../permissions/useRequestRecordPermissions'
import { useUpdateRequestConfidentiality } from '../../../permissions/useUpdateRequestConfidentiality'

interface Props {
  requestId: string
  isConfidential: boolean | undefined
  onClose?: () => void
}

/**
 * Mark/unmark the request as confidential. Hidden (not disabled) unless the permissions service
 * resolves `canPerformAdminTasks` for the caller — the same check the endpoint enforces. Both
 * directions take a confirmation: marking cuts broad readers immediately (the marker included when
 * they lack confidential access themselves), and unmarking re-exposes the record to everyone it
 * may have been deliberately hidden from.
 */
export const ConfidentialMenuItem = ({ requestId, isConfidential, onClose }: Props) => {
  const { t } = useTranslation('requests')
  const fgaEnabled = useFeatureFlags()[FeatureFlagsEnum.FGA_POC] === true
  const recordPermissions = useRequestRecordPermissions(requestId, { enabled: fgaEnabled })
  const { mutate, isPending } = useUpdateRequestConfidentiality(requestId)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  if (recordPermissions.status !== 'resolved' || !recordPermissions.permissions.canPerformAdminTasks) return null
  if (isConfidential == null) return null

  const closeDialog = () => setIsDialogOpen(false)
  const handleClick = () => {
    setIsDialogOpen(true)
    onClose?.()
  }
  const handleConfirm = () => {
    mutate(!isConfidential)
    closeDialog()
  }

  return (
    <>
      {/* Disabled while the flip and its refetch are in flight, so reopening the menu against the
          stale flag cannot queue a second submit. */}
      <MenuItem onClick={handleClick} disabled={isPending} data-testid="confidential-request-menu-item">
        <ListItemIcon>{isConfidential ? <LockOpenOutlined /> : <LockOutlined />}</ListItemIcon>
        <ListItemText>
          {isConfidential ? t('manage-request.unmark-confidential') : t('manage-request.mark-confidential')}
        </ListItemText>
      </MenuItem>
      <ConfirmDialog
        open={isDialogOpen}
        onClose={closeDialog}
        onConfirm={handleConfirm}
        title={isConfidential ? t('confidentiality.unmark-confirm-title') : t('confidentiality.confirm-title')}
        bodyTexts={
          isConfidential
            ? [t('confidentiality.unmark-confirm-description')]
            : [t('confidentiality.confirm-description'), t('confidentiality.confirm-self-lockout-warning')]
        }
        actions={[
          { variant: 'contained', color: 'secondary', text: t('confidentiality.confirm-cancel'), action: 'cancel' },
          {
            variant: 'contained',
            color: 'error',
            text: isConfidential ? t('confidentiality.unmark-confirm-remove') : t('confidentiality.confirm-mark'),
            action: 'confirm',
          },
        ]}
      />
    </>
  )
}
